import { PURCHASE_POINTS_API_ENDPOINTS } from "@/features/purchasePoints/config/purchasePointsApiPath";
import type { PurchasePointsBalance } from "@/features/purchasePoints/types/purchasePointsBalance.type";
import type {
  PurchasePointsConversion,
  PurchasePointsConversionsResponse,
} from "@/features/purchasePoints/types/purchasePointsConversion.type";
import type {
  PurchasePointsBrandOffersResponse,
  PurchasePointsBrandsResponse,
  PurchasePointsHighlightedOffersResponse,
  PurchasePointsOfferDetail,
  PurchasePointsOfferResponse,
} from "@/features/purchasePoints/types/purchasePointsOffer.type";
import type { PurchasePointsRewardsResponse } from "@/features/purchasePoints/types/purchasePointsReward.type";
import { fetcher } from "@/shared/lib/fetcher.client";

/**
 * The employee is derived from the token: no account id is ever sent. The
 * backend accepts `limit` as an alias of `per_page`, the front uses only one.
 */
export const purchasePointsService = {
  getOffer: async (offerId: string): Promise<PurchasePointsOfferDetail> => {
    const response = await fetcher.get<PurchasePointsOfferResponse>(
      PURCHASE_POINTS_API_ENDPOINTS.GET_OFFER(offerId),
    );
    return response.data.offer;
  },

  getBrands: async (page: number, perPage: number, searchTerm: string): Promise<PurchasePointsBrandsResponse> => {
    const response = await fetcher.get(PURCHASE_POINTS_API_ENDPOINTS.GET_BRANDS, {
      // An empty term is no search: axios leaves an undefined param out of the URL.
      params: { page, per_page: perPage, q: searchTerm || undefined },
    });
    return response.data;
  },

  getBrandOffers: async (brandSlug: string): Promise<PurchasePointsBrandOffersResponse> => {
    const response = await fetcher.get(PURCHASE_POINTS_API_ENDPOINTS.GET_BRAND(brandSlug));
    return response.data;
  },

  getHighlightedOffers: async (): Promise<PurchasePointsHighlightedOffersResponse> => {
    const response = await fetcher.get(PURCHASE_POINTS_API_ENDPOINTS.GET_HIGHLIGHTED_OFFERS);
    return response.data;
  },

  getBalance: async (): Promise<PurchasePointsBalance> => {
    const response = await fetcher.get(PURCHASE_POINTS_API_ENDPOINTS.GET_BALANCE);
    return response.data;
  },

  getRewards: async (page: number, perPage: number): Promise<PurchasePointsRewardsResponse> => {
    const response = await fetcher.get(PURCHASE_POINTS_API_ENDPOINTS.GET_REWARDS, { params: { page, per_page: perPage } });
    return response.data;
  },

  getConversions: async (page: number, perPage: number): Promise<PurchasePointsConversionsResponse> => {
    const response = await fetcher.get(
      PURCHASE_POINTS_API_ENDPOINTS.GET_CONVERSIONS, { params: { page, per_page: perPage } },
    );
    return response.data;
  },

  // No body at all: the conversion takes the whole available balance, and
  // sending an amount would suggest the server reads one.
  createConversion: async (): Promise<PurchasePointsConversion> => {
    const response = await fetcher.post(PURCHASE_POINTS_API_ENDPOINTS.CREATE_CONVERSION);
    return response.data;
  },
};
